import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { Wordmark } from "@/components/shell/wordmark";
import { VILLA_ASSETS } from "@/components/landing/assets";
import { afterLogin } from "@/lib/after-login";
import { said, saidByAuth } from "@/lib/errors";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

type Mode = "sign-in" | "sign-up" | "forgot";

const LEGAL_LINK =
  "text-ink-2 underline decoration-line-strong decoration-1 underline-offset-[3px] transition-colors hover:text-ink hover:decoration-ink";
// The picture sits on the landing's stage, so it is written in the stage's light.
const ON_STAGE = "text-[oklch(0.965_0.010_80)]";
const ROOM = "absolute inset-0 size-full object-cover object-[78%_50%]";

/**
 * Computers only: the landing's bedroom from one camera, arriving empty and furnishing itself, which is what Ours
 * does. Lazy, so a phone (where this is hidden) never downloads the pictures; `loading` has to stay ahead of `src`.
 */
function RoomStage() {
  const [furnished, setFurnished] = useState(false);
  return (
    <aside className="relative isolate hidden overflow-hidden bg-[oklch(0.165_0.014_60)] p-10 lg:sticky lg:top-0 lg:block lg:h-svh">
      <div aria-hidden className="absolute inset-0 -z-10">
        <img loading="lazy" src={`${VILLA_ASSETS}img/empty/walk-hd/0.webp`} alt="" fetchPriority="high" className={ROOM} />
        <img
          loading="lazy"
          src={`${VILLA_ASSETS}img/furnished.webp`}
          alt=""
          onLoad={() => setFurnished(true)}
          className={cn(
            ROOM,
            "transition-opacity delay-[1200ms] duration-[2400ms] ease-in-out motion-reduce:delay-0 motion-reduce:duration-0",
            furnished ? "opacity-100" : "opacity-0",
          )}
        />
        {/* Dark along the top so the mark reads on either picture. */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(18,14,10,.42),transparent_24%)]" />
      </div>
      <Wordmark className={ON_STAGE} />
    </aside>
  );
}

function LoginPage() {
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("sign-in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  // The page that sent them here, to go back to once they are in.
  const [next] = useState(() => afterLogin(window.location.search, window.location.origin));

  // A failed OAuth callback lands back here with ?error=<code> (and ?via=apple
  // for Apple's), a finished password reset with ?reset=done.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "done") {
      toast.success(t("reset.done"));
      window.history.replaceState(null, "", "/login");
    }
    const code = params.get("error");
    if (code === "account_not_linked") {
      toast.error(t("google.notLinked"), { duration: 12_000 });
    } else if (code) {
      const failed = params.get("via") === "apple" ? t("apple.failed") : t("google.failed");
      toast.error(`${failed} (${code})`);
    }
  }, [t]);

  async function onGoogle() {
    setGoogleBusy(true);
    try {
      const res = await authClient.signIn.social({
        provider: "google",
        callbackURL: next,
        errorCallbackURL: "/login",
      });
      if (res.error) throw new Error(saidByAuth(res.error, t("google.failed")));
      // Better Auth redirects to Google; if we reach here without a redirect
      // (e.g. popup flow), go on from here.
      navigate({ href: next });
    } catch (error) {
      toast.error(said(error, tc("status.somethingWentWrong")));
      setGoogleBusy(false);
    }
  }

  async function onApple() {
    setAppleBusy(true);
    try {
      const res = await authClient.signIn.social({
        provider: "apple",
        callbackURL: next,
        errorCallbackURL: "/login?via=apple",
      });
      if (res.error) throw new Error(saidByAuth(res.error, t("apple.failed")));
      navigate({ href: next });
    } catch (error) {
      toast.error(said(error, tc("status.somethingWentWrong")));
      setAppleBusy(false);
    }
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await authClient.requestPasswordReset({
          email: email.trim(),
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (res.error) throw new Error(saidByAuth(res.error, t("forgot.failed")));
        toast.success(t("forgot.sent"));
        setMode("sign-in");
        return;
      }
      if (mode === "sign-up") {
        const res = await authClient.signUp.email({
          email: email.trim(),
          password,
          name: name.trim() || email.split("@")[0],
          // Where the confirmation link in the welcome email leads.
          callbackURL: "/settings?email=confirmed",
        });
        if (res.error) throw new Error(saidByAuth(res.error, t("signUp.failed")));
        // A new account has no home yet: "/" takes it to onboarding first.
        navigate({ to: "/" });
        return;
      } else {
        const res = await authClient.signIn.email({
          email: email.trim(),
          password,
        });
        if (res.error) throw new Error(saidByAuth(res.error, t("signIn.failed")));
      }
      navigate({ href: next });
    } catch (error) {
      const message = said(error, tc("status.somethingWentWrong"));
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  const signUp = mode === "sign-up";
  const forgot = mode === "forgot";

  return (
    <>
      <main className="grid min-h-svh grid-cols-1 lg:grid-cols-[1fr_minmax(420px,34%)]">
        <RoomStage />

        {/* Form column */}
        <section className="flex flex-col px-5 py-8 sm:px-10 sm:py-12 lg:py-16">
          <div className="mb-8 sm:mb-10 lg:hidden">
            <Wordmark />
          </div>

          <div className="my-auto grid w-full max-w-sm gap-6 mx-auto sm:gap-7">
            <header className="grid gap-2">
              <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">
                {forgot
                  ? t("forgot.eyebrow")
                  : signUp
                    ? t("signUp.eyebrow")
                    : t("signIn.eyebrow")}
              </span>
              <h1 className="font-display text-[2rem] sm:text-3xl lg:text-4xl tracking-tight text-ink text-balance leading-[1.1]">
                <Trans
                  t={t}
                  i18nKey={
                    forgot ? "forgot.title" : signUp ? "signUp.title" : "signIn.title"
                  }
                  components={{ em: <span className="italic" /> }}
                />
              </h1>
              {forgot ? (
                <p className="text-sm leading-relaxed text-ink-2">{t("forgot.body")}</p>
              ) : null}
            </header>

            <div className={forgot ? "hidden" : "grid gap-4"}>
              <Button
                type="button"
                onClick={onApple}
                disabled={appleBusy || googleBusy || busy}
                size="lg"
                variant="outline"
                className="w-full gap-2.5"
              >
                <AppleLogo className="size-4 shrink-0" />
                <span className="truncate">
                  {appleBusy
                    ? t("google.redirecting")
                    : signUp
                      ? t("apple.continue")
                      : t("apple.signIn")}
                </span>
              </Button>
              <Button
                type="button"
                onClick={onGoogle}
                disabled={googleBusy || appleBusy || busy}
                size="lg"
                variant="outline"
                className="w-full gap-2.5"
              >
                <img
                  src="/brand-google.svg"
                  alt=""
                  aria-hidden
                  className="size-4 shrink-0"
                />
                <span className="truncate">
                  {googleBusy
                    ? t("google.redirecting")
                    : signUp
                      ? t("signUp.google")
                      : t("signIn.google")}
                </span>
              </Button>

              <div className="flex items-center gap-3 text-2xs uppercase tracking-[0.18em] text-ink-4">
                <span aria-hidden className="h-px flex-1 bg-line" />
                {t("divider")}
                <span aria-hidden className="h-px flex-1 bg-line" />
              </div>
            </div>

            <form onSubmit={onSubmit} className="grid gap-4 sm:gap-5">
              {signUp ? (
                <Field>
                  <Label htmlFor="name">{t("fields.name")}</Label>
                  <Input
                    id="name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("fields.namePlaceholder")}
                  />
                </Field>
              ) : null}
              <Field>
                <Label htmlFor="email">{t("fields.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("fields.emailPlaceholder")}
                  required
                />
              </Field>
              {forgot ? null : (
                <Field>
                  <div className="flex items-baseline justify-between gap-3">
                    <Label htmlFor="password">{t("fields.password")}</Label>
                    {/* Always offered: a signed-out page can't ask the backend
                        whether emails are sent (the Convex client waits for a
                        session). Where they aren't, asking says so. */}
                    {!signUp ? (
                      <button
                        type="button"
                        onClick={() => setMode("forgot")}
                        className="text-xs text-ink-3 underline decoration-line-strong underline-offset-[3px] transition-colors hover:text-ink hover:decoration-ink"
                      >
                        {t("signIn.forgot")}
                      </button>
                    ) : null}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete={signUp ? "new-password" : "current-password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </Field>
              )}
              <Button
                type="submit"
                disabled={busy || googleBusy || appleBusy}
                size="lg"
                className="mt-1 w-full"
              >
                {busy
                  ? "…"
                  : forgot
                    ? t("forgot.submit")
                    : signUp
                      ? t("signUp.submit")
                      : t("signIn.submit")}
              </Button>
            </form>

            <div className="border-t border-line pt-5">
              <button
                type="button"
                onClick={() => setMode(mode === "sign-in" ? "sign-up" : "sign-in")}
                className="text-sm text-ink-2 hover:text-ink underline underline-offset-[3px] decoration-line-strong hover:decoration-ink transition-colors"
              >
                {forgot
                  ? t("forgot.back")
                  : signUp
                    ? t("signUp.switch")
                    : t("signIn.switch")}
              </button>
            </div>

            <p className="text-xs leading-relaxed text-ink-3">
              <Trans
                t={t}
                i18nKey="legal"
                components={{
                  terms: <Link to="/terms" className={LEGAL_LINK} />,
                  privacy: <Link to="/privacy" className={LEGAL_LINK} />,
                }}
              />
            </p>
          </div>
        </section>
      </main>
      <Toaster />
    </>
  );
}

function Field({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-1.5">{children}</div>;
}

/** Apple's logo for the Sign in with Apple button, in the button's ink. */
function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.913 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}
